"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const PAGE_ID_PREFIX = "syntax";

interface ExampleCard {
  query: string;
  description: string;
}

interface SectionData {
  id: string;
  label: string;
  title: string;
  description: string;
  keywords: string[];
  examples: ExampleCard[];
}

function renderDescription(text: string): ReactNode[] {
  const parts = text.split(/(`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <code
          key={i}
          className="rounded bg-slate-800 px-1 py-0.5 text-xs text-slate-100"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

const SECTIONS: SectionData[] = [
  {
    id: "sets",
    label: "Sets",
    title: "Sets",
    description:
      "You can filter cards from a specific set using the `set:` or `s:` keyword. " +
      "Use the official set code (e.g. `OP01`, `ST07`, `EB02`, `PRB01`). " +
      "Set codes are case-insensitive.",
    keywords: ["set:", "s:"],
    examples: [
      {
        query: "set:OP01",
        description: "All cards from ROMANCE DAWN (OP-01)",
      },
      {
        query: "s:ST07",
        description: "All cards from STARTER DECK Big Mom Pirates (ST-07)",
      },
      {
        query: "set:EB02 t:leader",
        description: "Leaders from the EXTRA BOOSTER Anime 25th collection",
      },
    ],
  },
  {
    id: "types",
    label: "Card Types",
    title: "Card Types",
    description:
      "Filter cards by their class using the `t:` or `type:` keyword. " +
      "The type field matches against the card class: LEADER, CHARACTER, EVENT, STAGE, and DON!!. " +
      "The match is a substring search, so `t:char` will also match CHARACTER.",
    keywords: ["t:", "type:"],
    examples: [
      {
        query: "t:leader",
        description: "All Leader cards",
      },
      {
        query: "t:character c:red",
        description: "Red Character cards",
      },
      {
        query: "t:event set:OP03",
        description: "Event cards from OP-03",
      },
      {
        query: "t:stage",
        description: "All Stage cards",
      },
      {
        query: "t:DON!!",
        description: "DON!! cards",
      },
    ],
  },
  {
    id: "names",
    label: "Card Names",
    title: "Card Names",
    description:
      "Search for cards by name using the `name:` keyword, or simply type any word without a keyword prefix. " +
      "Free text that doesn't match a known field prefix is treated as a name search. " +
      'Use quotes around multi-word names: `name:"Monkey.D.Luffy"`. ' +
      "Name matching is case-insensitive and uses substring search.",
    keywords: ["name:", "(free text)"],
    examples: [
      {
        query: "luffy",
        description: 'Cards with "luffy" in their name',
      },
      {
        query: "name:zoro",
        description: 'Cards with "zoro" in their name',
      },
      {
        query: 'name:"Nico Robin"',
        description: 'Cards with "Nico Robin" in their name',
      },
    ],
  },
  {
    id: "colors",
    label: "Colors",
    title: "Colors",
    description:
      "Filter cards by color using the `c:` or `color:` keyword. " +
      "Accepts full color names (`red`, `blue`, `green`, `black`, `yellow`, `purple`) or single-letter shortcuts. " +
      "The single-letter shortcuts are: `r` (Red), `u` (Blue), `g` (Green), `b` (Black), `y` (Yellow), `p` (Purple).",
    keywords: ["c:", "color:"],
    examples: [
      {
        query: "c:red",
        description: "Red cards",
      },
      {
        query: "c:u",
        description: "Blue cards (u = Blue)",
      },
      {
        query: "c:green t:leader",
        description: "Green Leader cards",
      },
      {
        query: "c:p",
        description: "Purple cards (p = Purple)",
      },
      {
        query: "c:y set:ST07",
        description: "Yellow cards from ST-07",
      },
    ],
  },
  {
    id: "attributes",
    label: "Attributes",
    title: "Attributes (Combat Type)",
    description:
      "Filter cards by their combat attribute using the `attribute:`, `attr:`, or `a:` keyword. " +
      "Attribute values include `Strike`, `Ranged`, `Slash`, `Wisdom`, and `Special`. " +
      "Matching is case-insensitive and uses substring search.",
    keywords: ["attribute:", "attr:", "a:"],
    examples: [
      {
        query: "a:strike",
        description: "Cards with the Strike attribute",
      },
      {
        query: "attr:ranged c:blue",
        description: "Blue cards with the Ranged attribute",
      },
      {
        query: "a:slash t:character",
        description: "Characters with the Slash attribute",
      },
      {
        query: "attribute:wisdom set:OP01",
        description: "Wisdom cards from OP-01",
      },
      {
        query: "a:special",
        description: "Cards with the Special attribute",
      },
    ],
  },
  {
    id: "card-text",
    label: "Card Text",
    title: "Card Text (Oracle)",
    description:
      "Search within the card effect text using the `o:` or `oracle:` keyword. " +
      "This searches the printed effect/rules text of the card. " +
      "Matching is case-insensitive and uses substring search.",
    keywords: ["o:", "oracle:"],
    examples: [
      {
        query: "o:blocker",
        description: 'Cards whose effect text mentions "blocker"',
      },
      {
        query: "o:draw t:event",
        description: "Event cards that mention drawing",
      },
      {
        query: 'o:"end of your turn"',
        description: 'Cards with "end of your turn" in their text',
      },
    ],
  },
  {
    id: "numeric",
    label: "Numeric Filters",
    title: "Numeric Filters (Cost, Power, Counter)",
    description:
      "Filter cards by their numeric values using `cost`, `pow` (or `power`), and `counter` keywords. " +
      "These fields support comparison operators: `=`, `!=`, `<`, `<=`, `>`, `>=`. " +
      "You can combine multiple numeric filters to find cards within a specific range. " +
      "Note: Leader cards have a special cost format like `Life4` or `Life5` instead of a numeric cost, " +
      "so numeric cost filters will not match Leaders.",
    keywords: ["cost", "pow:", "power:", "counter"],
    examples: [
      {
        query: "cost>=3 cost<=5",
        description: "Cards with cost between 3 and 5",
      },
      {
        query: "pow>=7000",
        description: "Cards with 7000 or more power",
      },
      {
        query: "counter>0",
        description: "Cards with a positive counter value",
      },
      {
        query: "cost<=2 c:red t:character",
        description: "Red Characters that cost 2 or less",
      },
      {
        query: "pow>=8000 cost<=4",
        description: "High-power, low-cost cards",
      },
    ],
  },
  {
    id: "features",
    label: "Features",
    title: "Features and Traits",
    description:
      "Filter cards by their feature/trait tags using the `feature:` keyword. " +
      'Features include character affiliations like "Straw Hat Crew", "Supernovas", "Navy", etc. ' +
      "Use quotes around multi-word features. Matching is case-insensitive and uses substring search.",
    keywords: ["feature:"],
    examples: [
      {
        query: 'feature:"Straw Hat Crew"',
        description: "Cards with the Straw Hat Crew feature",
      },
      {
        query: "feature:Supernovas t:leader",
        description: "Leaders with the Supernovas feature",
      },
      {
        query: "feature:Navy c:blue",
        description: "Blue cards with the Navy feature",
      },
    ],
  },
  {
    id: "triggers",
    label: "Triggers & Counters",
    title: "Triggers and Counters",
    description:
      "Check whether cards have a trigger or counter using the `has:` keyword. " +
      "Use `has:trigger` to find cards with a trigger effect, or `has:counter` for cards with a counter value " +
      "OR a `[Counter]` effect in their text (e.g. counter events like Gum-Gum Rain). " +
      "You can also search within trigger text using `trigger:` (or `tr:`). " +
      "Prefix with `-` to negate: `-has:trigger` finds cards without a trigger.",
    keywords: ["has:trigger", "has:counter", "-has:trigger", "-has:counter", "trigger:", "tr:"],
    examples: [
      {
        query: "has:trigger",
        description: "Cards that have a trigger effect",
      },
      {
        query: "-has:trigger",
        description: "Cards without a trigger effect",
      },
      {
        query: "has:counter",
        description: "Cards with a counter value or [Counter] in their effect",
      },
      {
        query: "has:counter t:event",
        description: "Event cards with a [Counter] effect (e.g. Gum-Gum Rain, Divine Departure)",
      },
      {
        query: "trigger:life",
        description: 'Cards whose trigger text contains "life"',
      },
    ],
  },
  {
    id: "block-icon",
    label: "Block Icon",
    title: "Block Icon",
    description:
      "Filter cards by their block icon value using the `blockicon:`, `block:`, or `bi:` keyword. " +
      "Block icon is a numeric field (typically `1`, `2`, or `3`) and supports comparison operators: " +
      "`=`, `!=`, `<`, `<=`, `>`, `>=`.",
    keywords: ["blockicon:", "block:", "bi:"],
    examples: [
      {
        query: "bi:2",
        description: "Cards with block icon 2",
      },
      {
        query: "block:>=2",
        description: "Cards with block icon 2 or higher",
      },
      {
        query: "bi:1 c:red",
        description: "Red cards with block icon 1",
      },
    ],
  },
  {
    id: "art",
    label: "Art Variants",
    title: "Art Variants",
    description:
      "Filter for special art versions using the `art:` keyword. " +
      "Three values are supported: `alternate` (parallel/alternate art prints), " +
      "`manga` (manga-style art), and `unique` (any of alternate, manga, or SEC rarity).",
    keywords: ["art:alternate", "art:manga", "art:unique"],
    examples: [
      {
        query: "art:alternate",
        description: "Parallel/alternate art cards",
      },
      {
        query: "art:manga",
        description: "Manga art cards",
      },
      {
        query: "art:unique set:OP01",
        description: "Special art variants from OP-01",
      },
    ],
  },
  {
    id: "keywords",
    label: "Keywords",
    title: "Keywords",
    description:
      "Filter by game keywords using the `k:` or `keyword:` keyword. " +
      "Currently supports `rush` and `blocker`. " +
      "This will be expanded as the data model grows.",
    keywords: ["k:", "keyword:"],
    examples: [
      {
        query: "k:rush",
        description: "Cards with the Rush keyword",
      },
      {
        query: "k:blocker c:blue",
        description: "Blue cards with the Blocker keyword",
      },
    ],
  },
  {
    id: "card-id",
    label: "Card ID",
    title: "Card ID and Number",
    description:
      "Look up a specific card by its ID or number using the `id:` keyword. " +
      "You can also type a card number directly (e.g. `OP13-110`) without a prefix and it will be " +
      "automatically recognized as an ID search. Matches against both the full card ID and the card number.",
    keywords: ["id:", "(card number)"],
    examples: [
      {
        query: "id:OP01-001",
        description: "The specific card OP01-001",
      },
      {
        query: "OP13-110",
        description: "Auto-detected as card number OP13-110",
      },
    ],
  },
  {
    id: "rarity",
    label: "Rarity",
    title: "Rarity",
    description:
      "Filter by rarity using the `r:` keyword. " +
      "Rarity codes: `C` (Common), `UC` (Uncommon), `R` (Rare), `SR` (Super Rare), " +
      "`SEC` (Secret), `L` (Leader), `P` (Promo), `SP CARD` (Special). " +
      "You can also use full names: `r:common`, `r:uncommon`, `r:rare`, `r:super`, `r:secret`, `r:promo`, `r:leader`, `r:special`. " +
      "All P-xxx numbered cards are automatically tagged as Promo rarity.",
    keywords: ["r:", "r:promo", "r:common", "r:rare", "r:super", "r:secret"],
    examples: [
      {
        query: "r:sr",
        description: "Super Rare cards",
      },
      {
        query: "r:sec set:OP01",
        description: "Secret Rare cards from OP-01",
      },
      {
        query: "r:p",
        description: "Promo cards (all P-xxx numbered cards)",
      },
      {
        query: "r:promo",
        description: "Same as r:p — Promo cards using full name",
      },
      {
        query: "r:l",
        description: "Leader cards (by rarity type)",
      },
    ],
  },
  {
    id: "boolean",
    label: "Boolean Logic",
    title: "Boolean Logic (AND, OR, NOT, Grouping)",
    description:
      "Combine multiple filters with boolean logic. " +
      "Space-separated terms are ANDed together by default. " +
      "Use `or` between terms for OR logic. " +
      "Prefix any term with `-` to negate it (NOT). " +
      "Use parentheses `( )` to group complex conditions and control precedence.",
    keywords: ["(space)", "or", "-", "( )"],
    examples: [
      {
        query: "set:EB01 t:leader c:red cost<=4",
        description: "AND: Red Leaders from EB-01 that cost 4 or less",
      },
      {
        query: "name:luffy or name:zoro",
        description: "OR: Cards named Luffy or Zoro",
      },
      {
        query: "set:OP03 -r:sr",
        description: "NOT: Cards from OP-03 that are not Super Rare",
      },
      {
        query: "(t:leader or t:character) c:r",
        description: "Grouping: Red Leaders or Characters",
      },
      {
        query: "set:OP01 (t:leader or t:character)",
        description: "Leaders or Characters from OP-01",
      },
    ],
  },
];

function ExampleCardComponent({ query, description }: ExampleCard) {
  return (
    <Link
      href={`/search?q=${encodeURIComponent(query)}`}
      className="group block rounded-lg border border-slate-700/60 bg-slate-950/60 px-3 py-2 transition-colors hover:border-blue-500/50 hover:bg-slate-900/80"
    >
      <code className="text-xs font-medium text-blue-400 group-hover:text-blue-300">
        {query}
      </code>
      <p className="mt-1 text-xs text-slate-400">
        {description}
      </p>
    </Link>
  );
}

function SyntaxSection({ section }: { section: SectionData }) {
  return (
    <section
      id={`${PAGE_ID_PREFIX}-section-${section.id}`}
      className="scroll-mt-24 rounded-xl border border-slate-800 bg-slate-900/70"
    >
      <div className="flex flex-col gap-4 p-5 md:flex-row md:gap-8">
        {/* Left: title, description, keywords */}
        <div className="flex-1 space-y-3">
          <h2
            id={`${PAGE_ID_PREFIX}-title-${section.id}`}
            className="text-base font-semibold text-slate-100"
          >
            {section.title}
          </h2>
          <p className="text-sm leading-relaxed text-slate-300">
            {renderDescription(section.description)}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {section.keywords.map((kw) => (
              <span
                key={kw}
                className="rounded-md bg-slate-800 px-2 py-0.5 text-xs font-mono text-slate-200"
              >
                {kw}
              </span>
            ))}
          </div>
        </div>

        {/* Right: example cards */}
        <div className="flex w-full flex-col gap-2 md:w-80 md:shrink-0">
          {section.examples.map((ex) => (
            <ExampleCardComponent key={ex.query} {...ex} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function SyntaxPage() {
  const scrollTo = (sectionId: string) => {
    const el = document.getElementById(`${PAGE_ID_PREFIX}-section-${sectionId}`);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div
      id={`${PAGE_ID_PREFIX}-page`}
      className="flex min-h-screen flex-col bg-slate-950 text-slate-100"
    >
      <Header idPrefix={`${PAGE_ID_PREFIX}-header`} />

      <main
        id={`${PAGE_ID_PREFIX}-main`}
        className="mx-auto flex w-full max-w-5xl flex-1 flex-col gap-6 px-4 py-8"
      >
        {/* Hero */}
        <div className="text-center">
          <h2
            id={`${PAGE_ID_PREFIX}-hero-title`}
            className="text-2xl font-bold text-slate-50"
          >
            Punk Records Search Reference
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-slate-400">
            Punk Records uses a powerful text syntax to search the One Piece Card
            Game library. Jump to a section below:
          </p>
        </div>

        {/* Jump-to navigation */}
        <div
          id={`${PAGE_ID_PREFIX}-jump-nav`}
          className="flex flex-wrap justify-center gap-2"
        >
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              id={`${PAGE_ID_PREFIX}-jump-${s.id}`}
              type="button"
              onClick={() => scrollTo(s.id)}
              className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1 text-xs text-slate-200 transition-colors hover:border-blue-500/50 hover:bg-slate-800 hover:text-blue-400"
            >
              {s.label}
            </button>
          ))}
        </div>

        <hr className="border-slate-800" />

        {/* Sections */}
        {SECTIONS.map((s) => (
          <SyntaxSection key={s.id} section={s} />
        ))}

        {/* Footer note */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 text-center text-sm text-slate-400">
          You can type these queries directly on{" "}
          <Link href="/search" className="text-blue-400 hover:underline">
            /search
          </Link>
          , or use the{" "}
          <Link href="/advanced" className="text-blue-400 hover:underline">
            Advanced search
          </Link>{" "}
          page to build queries with buttons and sliders.
        </div>
      </main>

      <Footer idPrefix={`${PAGE_ID_PREFIX}-footer`} />
    </div>
  );
}
